/**
 * One-click board report download.
 *
 * Generates the executive PDF (GET /api/reports/board) and hands it to the
 * browser. Hidden from users without `report.read` — politeness only; the
 * server enforces the capability regardless.
 */

import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { downloadFile } from '../lib/download';
import { useAuth } from '../lib/auth';
import { useApiError } from '../lib/useApiError';
import { Button, ErrorNotice, Spinner } from './ui';
import { IconReport } from './icons';

export function BoardReportButton() {
  const { t } = useTranslation();
  const { allows } = useAuth();
  const describeError = useApiError();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const abort = useRef<AbortController | null>(null);

  if (!allows('report.read')) return null;

  const run = async () => {
    abort.current?.abort();
    const controller = new AbortController();
    abort.current = controller;
    setBusy(true);
    setError(null);
    try {
      await downloadFile('/reports/board', 'board-report.pdf', '/reports', controller.signal);
    } catch (err) {
      if (!controller.signal.aborted) setError(describeError(err));
    } finally {
      if (abort.current === controller) setBusy(false);
    }
  };

  return (
    <div className="flex flex-col items-end gap-2">
      <Button variant="secondary" size="sm" onClick={() => void run()} disabled={busy} aria-busy={busy}>
        {busy ? <Spinner className="h-4 w-4" /> : <IconReport className="w-4 h-4" aria-hidden />}
        <span>
          {busy
            ? t('dashboard.boardReportGenerating', 'Generating…')
            : t('dashboard.boardReport', 'Board report')}
        </span>
      </Button>
      {error && <ErrorNotice message={error} />}
    </div>
  );
}
